/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import type { InsightSynthesis } from '@g17eco/types/insights-synthesis';
import { Card } from 'reactstrap';

interface Props {
  insights: InsightSynthesis[];
}

interface MetricCardProps {
  icon: string;
  value: string | number;
  label: string;
  color?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ icon, value, label, color = 'primary' }) => (
  <Card shadow='sm' hover='true' className='p-3'>
    <div className='d-flex align-items-center gap-3'>
      <div className={`text-${color}`}>
        <i className={`fal fa-${icon} fa-2x`} />
      </div>
      <div>
        <div className='text-xl strong'>{value}</div>
        <div className='text-sm text-ThemeTextMedium'>{label}</div>
      </div>
    </div>
  </Card>
);

export const InsightsMetrics: React.FC<Props> = ({ insights }) => {
  const metrics = useMemo(() => {
    const total = insights.length;

    // Total actions (recommendations)
    const totalActions = insights.reduce((sum, i) => sum + i.content.recommendations.length, 0);

    // Critical issues: threats with high priority
    const criticalIssues = insights.filter(
      i => i.content.swotCategory === 'threat' && i.content.priorityScore >= 7
    ).length;

    // Average priority (0-10)
    const avgPriority = insights.length > 0
      ? (insights.reduce((sum, i) => sum + i.content.priorityScore, 0) / insights.length).toFixed(1)
      : '0.0';

    return {
      total,
      totalActions,
      criticalIssues,
      avgPriority,
    };
  }, [insights]);

  return (
    <div className='insights-metrics mb-4'>
      <div className='row g-3'>
        <div className='col-md-3'>
          <MetricCard
            icon='lightbulb'
            value={metrics.total}
            label='TOTAL INSIGHTS'
            color='info'
          />
        </div>
        <div className='col-md-3'>
          <MetricCard
            icon='tasks'
            value={metrics.totalActions}
            label='TOTAL ACTIONS'
            color='primary'
          />
        </div>
        <div className='col-md-3'>
          <MetricCard
            icon='exclamation-triangle'
            value={metrics.criticalIssues}
            label='CRITICAL ISSUES'
            color='danger'
          />
        </div>
        <div className='col-md-3'>
          <MetricCard
            icon='chart-line'
            value={metrics.avgPriority}
            label='AVG PRIORITY'
            color='warning'
          />
        </div>
      </div>
    </div>
  );
};
